import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Download, Calendar, Layers, Package, Tag, AlertCircle, TrendingDown, Receipt, ChevronDown, ChevronUp
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useProductionExpenses } from '@/hooks/useProductionExpenses';
import { useOperationalExpenses } from '@/hooks/useOperationalExpenses';
import { exportToPDF } from '@/utils/exportPDF';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function FinanceLaporan() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedProdCategory, setExpandedProdCategory] = useState(0); // 0 by default to show top category
  const [expandedOpCategory, setExpandedOpCategory] = useState(0); // 0 by default to show top category

  const { expenses: prodExpenses = [] } = useProductionExpenses();
  const { expenses: opExpenses = [] } = useOperationalExpenses();

  const { data: inventoryItems = [] } = useQuery({
    queryKey: ['inventory-items'],
    queryFn: () => base44.entities.InventoryItem.list('item_name'),
  });

  const { data: requests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date')
  });

  // 1. Filter by date
  const filteredProd = useMemo(() => {
    return prodExpenses.filter(e => {
      const matchFrom = !dateFrom || e.date >= dateFrom;
      const matchTo = !dateTo || e.date <= dateTo;
      return matchFrom && matchTo;
    });
  }, [prodExpenses, dateFrom, dateTo]);

  const filteredOp = useMemo(() => {
    return opExpenses.filter(e => {
      const matchFrom = !dateFrom || e.date >= dateFrom;
      const matchTo = !dateTo || e.date <= dateTo;
      return matchFrom && matchTo;
    });
  }, [opExpenses, dateFrom, dateTo]);

  // 2. Aggregate Biaya Produksi by Category
  const prodByCategory = useMemo(() => {
    const grouped = {};
    filteredProd.forEach(exp => {
      let cat = exp.category || 'Lainnya';
      let subcat = exp.subCategory || 'Lainnya';

      if (cat === 'Lainnya' && exp.itemName) {
        const inv = inventoryItems.find(i => 
          (i.item_name && i.item_name.toLowerCase() === exp.itemName.toLowerCase()) || 
          (i.nama && i.nama.toLowerCase() === exp.itemName.toLowerCase())
        );
        if (inv && inv._type) {
           const typeStr = inv._type.toLowerCase();
           if (typeStr === 'botol') { cat = 'Packaging'; subcat = 'Botol'; }
           else if (typeStr === 'tutup') { cat = 'Packaging'; subcat = 'Tutup'; }
           else if (typeStr === 'spray') { cat = 'Packaging'; subcat = 'Spray'; }
           else if (typeStr === 'bahan') { cat = 'Bahan Cair'; subcat = 'Lainnya'; }
           else if (typeStr === 'item-tambahan') { cat = 'Item Tambahan'; subcat = 'Lainnya'; }
           else if (typeStr === 'packaging') { cat = 'Packaging'; subcat = 'Lainnya'; }
        }
      }

      if (!grouped[cat]) {
        grouped[cat] = {
          category: cat,
          totalPengeluaran: 0,
          items: []
        };
      }
      
      const qty = Number(exp.qty) || 1;
      const hargaBeli = Number(exp.hargaBeli) || (Number(exp.amount) / qty) || 0;
      const pengeluaran = qty * hargaBeli;

      grouped[cat].totalPengeluaran += pengeluaran;
      
      grouped[cat].items.push({
        ...exp,
        category: cat,
        subCategory: subcat,
        qty,
        hargaBeli,
        pengeluaran
      });
    });

    // Sort items inside each category by pengeluaran (descending)
    return Object.values(grouped).map(cat => {
      cat.items.sort((a, b) => b.pengeluaran - a.pengeluaran);
      return cat;
    }).sort((a, b) => b.totalPengeluaran - a.totalPengeluaran); // sort categories by pengeluaran
  }, [filteredProd, inventoryItems]);

  // 3. Aggregate Biaya Operasional by Category
  const opByCategory = useMemo(() => {
    const grouped = {};
    filteredOp.forEach(exp => {
      const cat = exp.category || 'Lainnya';
      if (!grouped[cat]) {
        grouped[cat] = {
          category: cat,
          totalPengeluaran: 0,
          items: []
        };
      }
      grouped[cat].totalPengeluaran += Number(exp.amount) || 0;
      grouped[cat].items.push(exp);
    });

    return Object.values(grouped).map(cat => {
      cat.items.sort((a, b) => b.amount - a.amount);
      return cat;
    }).sort((a, b) => b.totalPengeluaran - a.totalPengeluaran);
  }, [filteredOp]);

  const grandTotalProd = useMemo(() => prodByCategory.reduce((sum, c) => sum + c.totalPengeluaran, 0), [prodByCategory]);
  const grandTotalOp = useMemo(() => opByCategory.reduce((sum, c) => sum + c.totalPengeluaran, 0), [opByCategory]);

  // 4. Laporan PPN Logic
  const ppnBySupplier = useMemo(() => {
    const grouped = {};
    requests.forEach(req => {
      // Hanya ambil yang sudah dibayar dan punya PPN
      if (req.status !== 'Sudah Dibayar' || !req.ppn_nominal || Number(req.ppn_nominal) <= 0) return;
      
      const reqDate = req.created_date ? req.created_date.split('T')[0] : '';
      if (dateFrom && reqDate < dateFrom) return;
      if (dateTo && reqDate > dateTo) return;

      const supplierName = req.nama_sales || req.atas_nama || 'Vendor Tidak Diketahui';
      
      if (!grouped[supplierName]) {
        grouped[supplierName] = {
          supplier: supplierName,
          totalPpn: 0,
          items: []
        };
      }
      
      grouped[supplierName].totalPpn += Number(req.ppn_nominal);
      grouped[supplierName].items.push(req);
    });

    return Object.values(grouped).sort((a, b) => b.totalPpn - a.totalPpn);
  }, [requests, dateFrom, dateTo]);

  const grandTotalPpn = useMemo(() => ppnBySupplier.reduce((sum, s) => sum + s.totalPpn, 0), [ppnBySupplier]);

  // Export
  const handleExport = () => {
    exportToPDF({
      title: 'Laporan Biaya Produksi & Operasional',
      subtitle: `Periode: ${dateFrom || 'Semua'} s/d ${dateTo || 'Semua'}`,
      filename: `Laporan-Biaya-Satala`,
      columns: [
        { key: 'tipe', label: 'Tipe' },
        { key: 'cat', label: 'Kategori' },
        { key: 'item', label: 'Item / Deskripsi' },
        { key: 'pengeluaran', label: 'Pengeluaran' }
      ],
      rows: [
        ...filteredProd.map(p => ({ tipe: 'Produksi', cat: p.category, item: p.itemName || p.description, pengeluaran: `Rp ${fmt(p.amount)}` })),
        ...filteredOp.map(o => ({ tipe: 'Operasional', cat: o.category, item: o.description, pengeluaran: `Rp ${fmt(o.amount)}` }))
      ]
    });
  };

  const handleExportPPN = () => {
    const rows = [];
    ppnBySupplier.forEach(sup => {
      sup.items.forEach(item => {
        rows.push({
          supplier: sup.supplier,
          item: item.nama_item,
          tanggal: item.created_date ? item.created_date.split('T')[0] : '-',
          ppn: `Rp ${fmt(item.ppn_nominal)}`
        });
      });
    });

    exportToPDF({
      title: 'Laporan Rekapitulasi PPN',
      subtitle: `Periode: ${dateFrom || 'Semua'} s/d ${dateTo || 'Semua'}`,
      filename: `Laporan-PPN-Satala`,
      columns: [
        { key: 'supplier', label: 'Supplier / Vendor' },
        { key: 'item', label: 'Item Dibeli' },
        { key: 'tanggal', label: 'Tanggal' },
        { key: 'ppn', label: 'Nominal PPN' }
      ],
      rows
    });
  };

  return (
    <div className="space-y-6 pb-16 text-[#302621]">
      <Tabs defaultValue="biaya" className="w-full">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">Laporan Keuangan</h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            Lihat peringkat pengeluaran biaya dan rekapitulasi PPN dari vendor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TabsList className="h-10 bg-slate-100 rounded-xl border border-slate-200">
            <TabsTrigger value="biaya" className="text-xs font-bold rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#8C6208] data-[state=active]:shadow-sm">Biaya & Margin</TabsTrigger>
            <TabsTrigger value="ppn" className="text-xs font-bold rounded-lg data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm">Laporan PPN</TabsTrigger>
          </TabsList>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl p-5 flex flex-wrap gap-4 items-center mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#8C6208]" />
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Filter Tanggal:</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Dari:</span>
          <CustomDatePicker 
            value={dateFrom} 
            onChange={setDateFrom} 
            className="h-9 text-xs w-36" 
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">S/d:</span>
          <CustomDatePicker 
            value={dateTo} 
            onChange={setDateTo} 
            className="h-9 text-xs w-36" 
          />
        </div>
        {(dateFrom || dateTo) && (
          <Button 
            variant="ghost" 
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-xs h-9 text-slate-500 hover:text-slate-800"
          >
            Reset
          </Button>
        )}
      </Card>

      <TabsContent value="biaya" className="space-y-6 m-0">
        <div className="flex justify-end mb-2">
          <Button onClick={handleExport} className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-9 shadow-sm">
            <Download className="w-4 h-4" /> Cetak Laporan Biaya
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* BIAYA PRODUKSI SECTION */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 border-b-2 border-emerald-500 pb-2 flex-1">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Layers className="w-4 h-4 text-emerald-700" />
            </div>
            <h2 className="text-sm md:text-xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              Peringkat Biaya Produksi
              <span className="text-[10px] md:text-sm font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full ml-2">
                Rp {fmt(grandTotalProd)}
              </span>
            </h2>
          </div>

          {prodByCategory.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-100 border-dashed">
              <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-500">Belum ada data biaya produksi.</p>
            </div>
          ) : (
            prodByCategory.map((cat, catIdx) => {
              const isExpanded = expandedProdCategory === catIdx;
              return (
              <Card key={catIdx} className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'border-emerald-200 shadow-md ring-1 ring-emerald-500/10' : 'border-slate-200 shadow-sm hover:border-emerald-200'} rounded-2xl cursor-pointer`} onClick={() => setExpandedProdCategory(isExpanded ? null : catIdx)}>
                {/* Category Header */}
                <div className={`${isExpanded ? 'bg-gradient-to-br from-emerald-50 to-green-50' : 'bg-white hover:bg-slate-50'} p-5 flex items-center justify-between border-b ${isExpanded ? 'border-emerald-100' : 'border-transparent'}`}>
                  <div>
                    <h3 className={`text-sm font-black uppercase tracking-widest mb-1 flex items-center gap-2 ${isExpanded ? 'text-emerald-900' : 'text-slate-700'}`}>
                      <Package className={`w-4 h-4 ${isExpanded ? 'text-emerald-600' : 'text-slate-400'}`} />
                      Kategori: {cat.category}
                    </h3>
                    <p className={`text-[10px] font-bold uppercase ${isExpanded ? 'text-emerald-700/60' : 'text-slate-400'}`}>Total Pengeluaran Produksi</p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <p className={`text-lg md:text-xl font-black ${isExpanded ? 'text-emerald-700' : 'text-slate-700'}`}>Rp {fmt(cat.totalPengeluaran)}</p>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-emerald-400" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                  </div>
                </div>

                {/* Items Ranking */}
                {isExpanded && (
                  <div className="divide-y divide-slate-100 bg-white">
                    {cat.items.map((item, i) => (
                      <div key={i} className="p-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                        <div className="w-6 h-6 shrink-0 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                          #{i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">{item.itemName || item.description || 'Tanpa Nama'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-wider">
                              {item.subCategory}
                            </span>
                            <span className="text-[10px] font-medium text-slate-500">• {item.qty} pcs</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-sm font-black text-slate-700">
                            Rp {fmt(item.pengeluaran)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              );
            })
          )}
        </div>

        {/* BIAYA OPERASIONAL SECTION */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 border-b-2 border-rose-500 pb-2 flex-1">
            <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-rose-700" />
            </div>
            <h2 className="text-sm md:text-xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              Peringkat Biaya Operasional
              <span className="text-[10px] md:text-sm font-bold bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full ml-2">
                Rp {fmt(grandTotalOp)}
              </span>
            </h2>
          </div>

          {opByCategory.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-100 border-dashed">
              <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-500">Belum ada data biaya operasional.</p>
            </div>
          ) : (
            opByCategory.map((cat, catIdx) => {
              const isExpanded = expandedOpCategory === catIdx;
              return (
              <Card key={catIdx} className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'border-rose-200 shadow-md ring-1 ring-rose-500/10' : 'border-slate-200 shadow-sm hover:border-rose-200'} rounded-2xl cursor-pointer`} onClick={() => setExpandedOpCategory(isExpanded ? null : catIdx)}>
                {/* Category Header */}
                <div className={`${isExpanded ? 'bg-gradient-to-br from-rose-50 to-pink-50' : 'bg-white hover:bg-slate-50'} p-5 flex items-center justify-between border-b ${isExpanded ? 'border-rose-100' : 'border-transparent'}`}>
                  <div>
                    <h3 className={`text-sm font-black uppercase tracking-widest mb-1 flex items-center gap-2 ${isExpanded ? 'text-rose-900' : 'text-slate-700'}`}>
                      <Tag className={`w-4 h-4 ${isExpanded ? 'text-rose-600' : 'text-slate-400'}`} />
                      Kategori: {cat.category}
                    </h3>
                    <p className={`text-[10px] font-bold uppercase ${isExpanded ? 'text-rose-700/60' : 'text-slate-400'}`}>Total Pengeluaran OPEX</p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <p className={`text-lg md:text-xl font-black ${isExpanded ? 'text-rose-700' : 'text-slate-700'}`}>Rp {fmt(cat.totalPengeluaran)}</p>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-rose-400" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                  </div>
                </div>

                {/* Items Ranking */}
                {isExpanded && (
                  <div className="divide-y divide-slate-100 bg-white">
                    {cat.items.map((item, i) => (
                      <div key={i} className="p-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                        <div className="w-6 h-6 shrink-0 rounded bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-xs">
                          #{i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">{item.description || 'Tanpa Keterangan'}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{item.date}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-sm font-black text-slate-700">
                            Rp {fmt(item.amount)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              );
            })
          )}
        </div>

        </div>
      </TabsContent>

      <TabsContent value="ppn" className="space-y-6 m-0">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Receipt className="w-4 h-4 text-amber-700" />
            </div>
            <h2 className="text-sm md:text-xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              Rekapitulasi PPN Masukan
              <span className="text-[10px] md:text-sm font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full ml-2">
                Total Rp {fmt(grandTotalPpn)}
              </span>
            </h2>
          </div>
          <Button onClick={handleExportPPN} className="bg-amber-600 text-white hover:bg-amber-700 gap-2 text-xs font-bold rounded-xl h-9 shadow-sm">
            <Download className="w-4 h-4" /> Cetak Laporan PPN
          </Button>
        </div>

        {ppnBySupplier.length === 0 ? (
          <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-100 border-dashed mt-6">
            <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-500">Belum ada data PPN dari supplier untuk periode ini.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
            {ppnBySupplier.map((sup, idx) => (
              <Card key={idx} className="overflow-hidden border-slate-200 shadow-sm rounded-2xl flex flex-col">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 border-b border-amber-100 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-black text-amber-900 uppercase tracking-widest mb-1 truncate" title={sup.supplier}>
                      {sup.supplier}
                    </h3>
                    <p className="text-[10px] font-bold text-amber-700/60 uppercase">Total PPN Supplier Ini</p>
                  </div>
                  <div className="text-right shrink-0 pl-3">
                    <p className="text-lg font-black text-amber-700">Rp {fmt(sup.totalPpn)}</p>
                  </div>
                </div>
                <div className="divide-y divide-slate-100 bg-white flex-1 overflow-y-auto max-h-[300px]">
                  {sup.items.map((item, i) => (
                    <div key={i} className="p-4 hover:bg-slate-50 transition-colors flex items-center gap-3">
                      <div className="w-6 h-6 shrink-0 rounded bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate" title={item.nama_item}>{item.nama_item}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{item.created_date ? item.created_date.split('T')[0] : '-'}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-black text-slate-700">
                          Rp {fmt(item.ppn_nominal)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>
      </Tabs>
    </div>
  );
}
